import { objectKeys } from "app/helpers/objects";
import type { CrmFiltersModel } from "app/pages/sales-intelligence/domain/crm-integration/types/custom-filter-values";
import { modelToCrmFields } from "app/pages/sales-intelligence/domain/crm-integration/utils/custom-filters-dto-adapters";
import type { SignalsGroupedByTypeKey } from "app/pages/sales-intelligence/domain/signals/types/signal-collection";
import type { SignalTypeKey } from "app/pages/sales-intelligence/domain/signals/types/signal-key";
import { isSignalTypeKey } from "app/pages/sales-intelligence/domain/signals/utils/signal-key";
import { splitSignalsDTO } from "app/pages/sales-intelligence/domain/signals/utils/signals-dto-adapter";
import type {
    ILeadsSearchFiltersDto,
    LeadsSearchAllFiltersTypes,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

export const createCountriesFilterValue = (
    filtersValue: Partial<ILeadsSearchFiltersDto>,
): ILeadsSearchFiltersDto["leadsFilters"]["countries"] => filtersValue.leadsFilters?.countries;

/** Single source of truth for "the leads 'Visits From' selection is worldwide". */
export const isCountriesWorldwide = (countries: readonly number[] | undefined): boolean =>
    countries?.length === 1 && countries[0] === WORLDWIDE_COUNTRY_ID;

/** Scoring criteria support a single visits-from country; leads search allows multi-country OR. */
export const toSingleVisitsFromCountry = (countries: readonly number[]): readonly number[] =>
    countries.length <= 1 ? countries : [countries[0]];

export const createAppPlatformFilterValue = (
    filtersValue: Partial<ILeadsSearchFiltersDto>,
): ILeadsSearchFiltersDto["appsFilters"]["market"] => filtersValue.appsFilters?.market;

const countSignalsFilterValues = (signals?: ILeadsSearchFiltersDto["signalsFilters"]): number =>
    Object.values(signals ?? {}).flat().length;

// Applied ⟺ the CRM request array carries at least one item (a non-empty field). Replaces the old
// `inclusion !== "any"` toggle signal (SI-8738 / ADR-0010).
export const createIsCrmApplied = (model: CrmFiltersModel | undefined): boolean => (model?.length ?? 0) > 0;

/**
 * The single "which filters are applied" projection (flat panel space) every dirtiness selector derives from.
 */
export const getDirtyFilters = (filtersValue: Partial<ILeadsSearchFiltersDto>): Partial<LeadsSearchAllFiltersTypes> => {
    const {
        contactsFilters = {},
        leadsFilters = {},
        signalsFilters,
        appsFilters = {},
        salesforceCustomFilters,
    } = filtersValue;

    const { searchText: _contactsSearchText, ...contactsRest } = contactsFilters;
    const { searchText: _leadsSearchText, ...leadsRest } = leadsFilters;
    const { searchText: _appsSearchText, ...appsRest } = appsFilters;

    const crmFields = createIsCrmApplied(salesforceCustomFilters)
        ? modelToCrmFields(salesforceCustomFilters, "salesforce")
        : {};

    const base = { ...contactsRest, ...leadsRest, ...appsRest, ...crmFields };

    if (!countSignalsFilterValues(signalsFilters)) {
        return base;
    }

    return { ...base, ...splitSignalsDTO(signalsFilters) };
};

export const createDirtyFiltersFlag = (panelValues: Partial<LeadsSearchAllFiltersTypes>): boolean =>
    objectKeys(panelValues).some((key) => {
        const value = panelValues[key];

        if (isSignalTypeKey(key)) {
            return ((value as SignalsGroupedByTypeKey[SignalTypeKey])?.length ?? 0) > 0;
        }

        return value != null;
    });

/**
 * "Does this filter DTO carry any applied selection?" — the boolean companion to {@link getDirtyFilters},
 * for callers holding a raw DTO (e.g. the AI agent seed) rather than redux state. All category logic
 * (incl. CRM) lives in `getDirtyFilters`; this only flags whether anything survived the projection.
 */
export const createHasAppliedFilters = (filtersValue: Partial<ILeadsSearchFiltersDto>): boolean =>
    createDirtyFiltersFlag(getDirtyFilters(filtersValue));

export const createSavedSearchFiltersCount = (filtersValue: Partial<ILeadsSearchFiltersDto>): number => {
    const { contactsFilters = {}, leadsFilters = {} } = filtersValue;

    const { searchText: contactsSearchText, ...contactsRest } = contactsFilters;
    const { searchText: leadsSearchText, ...leadsRest } = leadsFilters;

    return Object.keys(contactsRest).length + Object.keys(leadsRest).length;
};
